import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { Input } from "@/lib/components/Input";
import { Button } from "@/lib/components/Button/Button";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  initialName?: string;
  placeholder?: string;
  submitLabel: string;
  isSubmitting: boolean;
  onSubmit: (name: string) => void;
};

export default function NameDialog({
  open,
  onOpenChange,
  ...formProps
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <NameDialogForm onOpenChange={onOpenChange} {...formProps} />
      </DialogContent>
    </Dialog>
  );
}

function NameDialogForm({
  onOpenChange,
  title,
  description,
  initialName = "",
  placeholder = "Name",
  submitLabel,
  isSubmitting,
  onSubmit,
}: Omit<Props, "open">) {
  const [name, setName] = useState(initialName);

  const trimmedName = name.trim();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!trimmedName || isSubmitting) return;
    onSubmit(trimmedName);
  }

  return (
    <>
      <DialogHeader className="w-full">
        <DialogTitle>{title}</DialogTitle>
        {description && <DialogDescription>{description}</DialogDescription>}
      </DialogHeader>
      <form onSubmit={handleSubmit} className="flex w-full flex-col">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder}
          autoFocus
        />
        <DialogFooter className="mt-8">
          <Button
            type="submit"
            size="sm"
            disabled={!trimmedName}
            isLoading={isSubmitting}
          >
            {submitLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
